import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Home, Building2, Search, Plus, Loader2, MoreHorizontal,
  FileText, Send, Upload, CheckCircle, Circle, Eye, Edit, Trash2,
  ArrowLeft, Filter
} from "lucide-react";
import { API } from "@/App";
import { AddInstructionModal, InstructionCard } from "@/components/instructions/AddInstructionModal";

const statusColors = {
  draft: "bg-slate-100 text-slate-700",
  pending_approval: "bg-amber-100 text-amber-700",
  approved: "bg-blue-100 text-blue-700",
  live: "bg-emerald-100 text-emerald-700",
  sold: "bg-purple-100 text-purple-700",
  let: "bg-purple-100 text-purple-700",
  withdrawn: "bg-red-100 text-red-700"
};

export const InstructionsListPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [instructions, setInstructions] = useState([]);
  const [filteredInstructions, setFilteredInstructions] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchInstructions();
  }, []);

  useEffect(() => {
    filterInstructions();
  }, [instructions, searchTerm, typeFilter, statusFilter]);

  const fetchInstructions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/instructions/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstructions(res.data.instructions || []);
    } catch (error) {
      console.error("Error fetching instructions:", error);
      toast.error("Failed to load instructions");
    } finally {
      setLoading(false);
    }
  };

  const filterInstructions = () => {
    let filtered = [...instructions];
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(inst => 
        inst.full_address?.toLowerCase().includes(term) ||
        inst.property_ref?.toLowerCase().includes(term) ||
        inst.applicant_name?.toLowerCase().includes(term)
      );
    }
    
    // Type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter(inst => inst.type === typeFilter);
    }
    
    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(inst => inst.status === statusFilter);
    }
    
    setFilteredInstructions(filtered);
  };

  const handleAction = async (instructionId, action) => {
    try {
      await axios.post(
        `${API}/instructions/${instructionId}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Action completed: ${action.replace("-", " ")}`);
      fetchInstructions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  const formatPrice = (instruction) => {
    const price = instruction.asking_price || instruction.monthly_rent || 0;
    const suffix = instruction.type === "lettings" ? " pcm" : "";
    return `£${price.toLocaleString()}${suffix}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  // Stats
  const stats = {
    total: instructions.length,
    sales: instructions.filter(i => i.type === "sales").length,
    lettings: instructions.filter(i => i.type === "lettings").length,
    draft: instructions.filter(i => i.status === "draft").length,
    live: instructions.filter(i => i.status === "live").length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Property Instructions</h1>
                <p className="text-sm text-slate-500">Manage all your property listings</p>
              </div>
            </div>
            <Button onClick={() => setShowAddModal(true)} data-testid="add-instruction-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Listing
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-700">{stats.total}</p>
              <p className="text-sm text-slate-500">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{stats.sales}</p>
              <p className="text-sm text-blue-600">Sales</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-700">{stats.lettings}</p>
              <p className="text-sm text-purple-600">Lettings</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-600">{stats.draft}</p>
              <p className="text-sm text-slate-500">Draft</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-700">{stats.live}</p>
              <p className="text-sm text-emerald-600">Live</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by address, ref, or applicant..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-instructions"
                  />
                </div>
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="lettings">Lettings</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                  <SelectItem value="let">Let</SelectItem>
                  <SelectItem value="withdrawn">Withdrawn</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Instructions Table */}
        <Card>
          <CardContent className="p-0">
            {filteredInstructions.length === 0 ? (
              <div className="p-12 text-center">
                <Home className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Instructions Found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || typeFilter !== "all" || statusFilter !== "all"
                    ? "Try adjusting your filters"
                    : "Create your first property listing"}
                </p>
                <Button onClick={() => setShowAddModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Listing
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInstructions.map((instruction) => (
                    <TableRow key={instruction.instruction_id} className="cursor-pointer hover:bg-slate-50">
                      <TableCell>
                        <div>
                          <p className="font-medium">{instruction.full_address}</p>
                          <p className="text-sm text-muted-foreground">
                            Ref: {instruction.property_ref}
                            {instruction.applicant_name && instruction.applicant_name !== "New Instruction" && (
                              <> • {instruction.applicant_name}</>
                            )}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={instruction.type === "sales" ? "border-blue-300 text-blue-700" : "border-purple-300 text-purple-700"}>
                          {instruction.type === "sales" ? (
                            <><Home className="w-3 h-3 mr-1" /> Sales</>
                          ) : (
                            <><Building2 className="w-3 h-3 mr-1" /> Lettings</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatPrice(instruction)}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[instruction.status] || "bg-slate-100"}>
                          {instruction.status?.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {instruction.draft_approved ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500" title="Draft Approved" />
                          ) : (
                            <Circle className="w-4 h-4 text-slate-300" title="Draft Pending" />
                          )}
                          {instruction.contract_signed ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500" title="Contract Signed" />
                          ) : (
                            <Circle className="w-4 h-4 text-slate-300" title="Contract Pending" />
                          )}
                          {instruction.ids_uploaded ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500" title="IDs Uploaded" />
                          ) : (
                            <Circle className="w-4 h-4 text-slate-300" title="IDs Pending" />
                          )}
                          {instruction.portal_live ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500" title="Live on Portals" />
                          ) : (
                            <Circle className="w-4 h-4 text-slate-300" title="Not Live" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(instruction.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {!instruction.draft_approved && instruction.status === "draft" && (
                              <DropdownMenuItem onClick={() => handleAction(instruction.instruction_id, "send-draft")}>
                                <Send className="w-4 h-4 mr-2" />
                                Send Draft for Approval
                              </DropdownMenuItem>
                            )}
                            {instruction.draft_approved && !instruction.contract_signed && (
                              <DropdownMenuItem onClick={() => handleAction(instruction.instruction_id, "send-contract")}>
                                <FileText className="w-4 h-4 mr-2" />
                                Send Contract
                              </DropdownMenuItem>
                            )}
                            {instruction.contract_signed && instruction.ids_uploaded && !instruction.portal_live && (
                              <DropdownMenuItem onClick={() => handleAction(instruction.instruction_id, "go-live")}>
                                <Upload className="w-4 h-4 mr-2" />
                                Go Live on Portals
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => navigate(`/sales/instruction-detail/${instruction.instruction_id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/sales/instruction-detail/${instruction.instruction_id}`)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Marketing
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Withdraw
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Instruction Modal */}
      <AddInstructionModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        applicantId={null}
        applicantName="New Listing"
        onSuccess={() => {
          fetchInstructions();
        }}
      />
    </div>
  );
};

export default InstructionsListPage;
